import { useEffect, useState } from 'react';
import { ArrowUpDown, Banknote, SearchX } from 'lucide-react';
import { cn, formatDateTime } from '@/lib/utils';
import { EmptyState } from '@/components/shared/empty-state';
import { PageHeader } from '@/components/shared/page-header';
import { StatusBadge } from '@/components/shared/status-badge';
import { DataTablePagination } from '@/components/data-table/data-table-pagination';
import { Button } from '@/components/ui/button';
import { SupernovaDatePicker, civilFromInstant, instantFromCivil, type DateRange } from '@/components/ui/date-picker';
import { useReportingTimeZone } from '@/hooks/use-reporting-timezone';
import { zonedTodayInput } from '@/lib/report-timezone';
import { useLocationStore } from '@/stores/location-store';
import { useFundingTransactions, type FundingTransactionRow } from '@/hooks/use-funding-transactions';

function formatCurrency(value: string | number): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  const formatted = Math.abs(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return num < 0 ? `-$${formatted}` : `$${formatted}`;
}

// Bridge the string 'YYYY-MM-DD' filter state to the range picker's Date
// values, resolved in the reporting zone — same bridge used by the SuperNova
// Reconciliation panel's date pickers.
const ymdToDate = (s: string, tz: string): Date | null => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  return y && m && d ? instantFromCivil({ year: y, month: m, day: d, hour: 0, minute: 0, second: 0 }, tz) : null;
};
const dateToYmd = (d: Date | null, tz: string): string => {
  if (!d) return '';
  const c = civilFromInstant(d, tz);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${c.year}-${p(c.month)}-${p(c.day)}`;
};

// Opens on the current month to date, less the two newest days (funding files
// lag): end = today − 2 in the reporting zone, start = the 1st of end's month.
// On the 1st/2nd of a month end falls back into the previous month and start
// follows it, so the page shows last month rather than an empty stub.
// Today 29 Sep → 01 Sep – 27 Sep; today 01 Oct → 01 Sep – 29 Sep.
const monthToDateRange = (tz: string): { dateFrom: string; dateTo: string } => {
  const [y, m, d] = zonedTodayInput(tz).split('-').map(Number);
  // Calendar arithmetic only — UTC keeps the subtraction free of DST shifts.
  const end = new Date(Date.UTC(y, m - 1, d - 2));
  const p = (n: number) => String(n).padStart(2, '0');
  const ym = `${end.getUTCFullYear()}-${p(end.getUTCMonth() + 1)}`;
  return { dateFrom: `${ym}-01`, dateTo: `${ym}-${p(end.getUTCDate())}` };
};

const COLUMNS: { value: string; label: string }[] = [
  { value: 'fundingDate', label: 'Funding Date' },
  { value: 'transactionNumber', label: 'Transaction #' },
  { value: 'authCode', label: 'Auth Code' },
  { value: 'lastFour', label: 'Card' },
  { value: 'amount', label: 'Amount' },
  { value: 'status', label: 'Status' },
];

function FundingTransactionsRow({ row }: { row: FundingTransactionRow }) {
  return (
    <tr className="border-b border-border last:border-0">
      <td className="px-4 py-3">{formatDateTime(row.fundingDate)}</td>
      <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{row.transactionNumber}</td>
      <td className="px-4 py-3 font-mono">{row.authCode}</td>
      <td className="px-4 py-3">{row.lastFour ? `**** ${row.lastFour}` : ''}</td>
      <td className="px-4 py-3 text-right font-semibold">{formatCurrency(row.amount)}</td>
      <td className="px-4 py-3">{row.status ? <StatusBadge status={row.status} /> : ''}</td>
    </tr>
  );
}

// Standalone page for the processor's funding-file data (cc_funding_target_rows)
// — the same listing already shown inside SuperNova Reconciliation's "Funded
// Transactions" tab, surfaced here as its own sidebar module so it can be
// gated independently (see tenant_modules 'funding_transactions').
export function FundingTransactionsPage() {
  const { selectedLocationId } = useLocationStore();
  const { timeZone: reportTz, label: reportTzLabel } = useReportingTimeZone();

  const [defaultRange] = useState(() => monthToDateRange(reportTz));
  const [dateFromInput, setDateFromInput] = useState(defaultRange.dateFrom);
  const [dateToInput, setDateToInput] = useState(defaultRange.dateTo);
  const [applied, setApplied] = useState<{ dateFrom: string; dateTo: string }>(defaultRange);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [sortBy, setSortBy] = useState(COLUMNS[0].value);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [appliedFilters, setAppliedFilters] = useState<Record<string, string>>({});

  useEffect(() => {
    const t = setTimeout(() => {
      setAppliedFilters(columnFilters);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [columnFilters]);

  const filtersParam = Object.keys(appliedFilters).length > 0 ? JSON.stringify(appliedFilters) : undefined;
  const listParams = {
    locationId: selectedLocationId || undefined,
    dateFrom: applied.dateFrom || undefined,
    dateTo: applied.dateTo || undefined,
    page,
    perPage,
    sortBy,
    sortDir,
    filters: filtersParam,
  };

  const { data, isLoading } = useFundingTransactions(listParams);
  const rows = data?.data ?? [];
  const pagination = data?.pagination;
  const hasFilters = Object.values(appliedFilters).some((v) => v.trim() !== '');

  const handleApply = () => {
    setApplied({ dateFrom: dateFromInput, dateTo: dateToInput });
    setPage(1);
  };
  const handleReset = () => {
    setDateFromInput(defaultRange.dateFrom);
    setDateToInput(defaultRange.dateTo);
    setApplied(defaultRange);
    setColumnFilters({});
    setAppliedFilters({});
    setSortBy(COLUMNS[0].value);
    setSortDir('desc');
    setPage(1);
  };
  const handleSortChange = (field: string) => {
    if (sortBy === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
    setPage(1);
  };
  const handleFilterChange = (field: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Transactions" description="Card transactions processed." />

      {/* Filter card — the same Date Range card and preset-first range picker
          as the Reports pages. The picker's own Apply only writes the draft
          range; the Apply button below runs the query. */}
      <div className="rounded-xl border border-border bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Date Range</h3>
          <span className="text-xs text-muted-foreground">Timezone: {reportTzLabel}</span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Funding date</label>
            <SupernovaDatePicker
              mode="range"
              presetFirst
              portal
              compactDisplay
              autoApply={false}
              clearable={false}
              timezone={reportTz}
              className="w-[300px] max-w-full"
              value={{ start: ymdToDate(dateFromInput, reportTz), end: ymdToDate(dateToInput, reportTz) }}
              onApply={(v) => {
                const r = (v as DateRange | null) ?? { start: null, end: null };
                setDateFromInput(dateToYmd(r.start, reportTz));
                setDateToInput(dateToYmd(r.end, reportTz));
              }}
            />
          </div>
          <div className="flex items-center gap-3">
            <Button size="sm" variant="primary" onClick={handleApply}>
              Apply
            </Button>
            <Button size="sm" variant="destructive-outline" onClick={handleReset}>
              Reset
            </Button>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                {COLUMNS.map((col) => {
                  const isSorted = sortBy === col.value;
                  return (
                    <th key={col.value} className="px-4 py-2 font-medium">
                      <button
                        type="button"
                        onClick={() => handleSortChange(col.value)}
                        className="flex items-center gap-1 whitespace-nowrap hover:text-foreground"
                      >
                        {col.label}
                        {isSorted ? (
                          <ArrowUpDown className={cn('h-3 w-3 text-primary', sortDir === 'asc' && 'rotate-180')} />
                        ) : (
                          <ArrowUpDown className="h-3 w-3 text-muted-foreground/40" />
                        )}
                      </button>
                    </th>
                  );
                })}
              </tr>
              <tr className="border-b border-border bg-white">
                {COLUMNS.map((col) => (
                  <th key={col.value} className="px-4 py-1.5 font-normal">
                    <input
                      type="text"
                      value={columnFilters[col.value] ?? ''}
                      onChange={(e) => handleFilterChange(col.value, e.target.value)}
                      placeholder="Filter…"
                      className="w-full min-w-[90px] rounded border border-input bg-white px-2 py-1 text-xs normal-case text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={COLUMNS.length} className="h-64 text-center text-muted-foreground">
                    Loading...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS.length}>
                    <EmptyState
                      icon={hasFilters ? SearchX : Banknote}
                      title={hasFilters ? 'No matching funding transactions' : 'No funding transactions found'}
                      description={
                        hasFilters
                          ? 'Try changing the filters or date range.'
                          : 'Funded transactions will appear here once the processor funding file is received.'
                      }
                    />
                  </td>
                </tr>
              ) : (
                rows.map((row) => <FundingTransactionsRow key={row.id} row={row} />)
              )}
            </tbody>
          </table>
        </div>
        {pagination && pagination.total > 0 && (
          <DataTablePagination
            page={pagination.page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            perPage={pagination.perPage}
            onPageChange={setPage}
            perPageOptions={[10, 25, 50, 100]}
            onPerPageChange={(val) => {
              setPerPage(val);
              setPage(1);
            }}
          />
        )}
      </div>
    </div>
  );
}
